/**
 * DIY browser device fingerprint collector (CF-2000 B06a).
 *
 * Zero external dependencies (per project `no_third_party` constraint).
 * Composite signal: canvas hash + UA + WebGL renderer + AudioContext hash
 * + screen size + language. Composite SHA-256 hex via Web Crypto.
 *
 * Failure resilience: any single signal failure (canvas blocked, WebGL
 * unavailable, audio context throws) falls back to a marker string; the
 * resulting hash is "degraded" but still stable per browser.
 */

import type { FingerprintComponents } from '@shared/device-fingerprint';

export interface CollectedFingerprint {
  hash_hex: string;
  components: FingerprintComponents;
}

const CANVAS_TEXT = 'Chat-fingerprint-v1 ⚡🎉 ABCxyz';

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function collectCanvas(): string {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 240;
    canvas.height = 60;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'canvas:unsupported';

    ctx.textBaseline = 'top';
    ctx.font = '14px "Arial"';
    ctx.fillStyle = '#f60';
    ctx.fillRect(0, 0, 240, 60);
    ctx.fillStyle = '#069';
    ctx.fillText(CANVAS_TEXT, 2, 2);
    ctx.fillStyle = 'rgba(102, 200, 0, 0.5)';
    ctx.fillText(CANVAS_TEXT, 4, 18);
    ctx.beginPath();
    ctx.arc(40, 40, 10, 0, 2 * Math.PI);
    ctx.stroke();

    return canvas.toDataURL();
  } catch {
    return 'canvas:error';
  }
}

function collectWebGLRenderer(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl');
    if (!gl || !(gl instanceof WebGLRenderingContext)) return 'webgl:unsupported';
    const dbgInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (dbgInfo) {
      const vendor = gl.getParameter(dbgInfo.UNMASKED_VENDOR_WEBGL) as string;
      const renderer = gl.getParameter(dbgInfo.UNMASKED_RENDERER_WEBGL) as string;
      return `${vendor}|${renderer}`;
    }
    return `${gl.getParameter(gl.VENDOR)}|${gl.getParameter(gl.RENDERER)}`;
  } catch {
    return 'webgl:error';
  }
}

async function collectAudioContextHash(): Promise<string> {
  try {
    const Ctor =
      (window as unknown as { OfflineAudioContext?: typeof OfflineAudioContext }).OfflineAudioContext ??
      (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
    if (!Ctor) return 'audio:unsupported';
    const ctx = new Ctor(1, 44100, 44100);
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(10000, ctx.currentTime);
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.setValueAtTime(-50, ctx.currentTime);
    compressor.knee.setValueAtTime(40, ctx.currentTime);
    compressor.ratio.setValueAtTime(12, ctx.currentTime);
    compressor.attack.setValueAtTime(0, ctx.currentTime);
    compressor.release.setValueAtTime(0.25, ctx.currentTime);
    osc.connect(compressor);
    compressor.connect(ctx.destination);
    osc.start(0);
    const buf = await ctx.startRendering();
    const channel = buf.getChannelData(0);
    // Sample-DCT-ish: sum subset of buffer values to get a stable scalar.
    let sum = 0;
    for (let i = 4500; i < 5000; i++) {
      sum += Math.abs(channel[i] ?? 0);
    }
    return sum.toFixed(8);
  } catch {
    return 'audio:error';
  }
}

function collectScreenSize(): string {
  // Round to nearest 100 to stabilize across minor zoom / DPR variations.
  const w = Math.round(window.screen.width / 100) * 100;
  const h = Math.round(window.screen.height / 100) * 100;
  return `${w}x${h}`;
}

function collectLanguage(): string {
  return navigator.language || 'unknown';
}

/**
 * Builds the components object + computes the composite SHA-256 hash.
 * Stable: same browser/device produces the same hash across visits.
 */
export async function collectDeviceFingerprint(): Promise<CollectedFingerprint> {
  const components: FingerprintComponents = {
    canvas: collectCanvas(),
    ua: navigator.userAgent,
    webgl_renderer: collectWebGLRenderer(),
    audio_context_hash: await collectAudioContextHash(),
    screen_size: collectScreenSize(),
    language: collectLanguage(),
  };

  // Canonical JSON: sort keys to ensure same content → same hash regardless
  // of object key iteration order.
  const asRecord = components as unknown as Record<string, string>;
  const sortedKeys = Object.keys(asRecord).sort();
  const ordered: Record<string, string> = {};
  for (const k of sortedKeys) {
    ordered[k] = asRecord[k] ?? '';
  }
  const canonical = JSON.stringify(ordered);

  const hash_hex = await sha256Hex(canonical);
  return { hash_hex, components };
}
